import {
  ActiveElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartDataset,
  ChartEvent,
  ChartType,
  Legend,
  LinearScale,
  Tooltip,
  TooltipModel,
} from 'chart.js';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';
import { memo, useCallback, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import styled, { useTheme } from 'styled-components';

import useMedia from '~hooks/useMedia';
import { IWikiChart } from '~types/company';
import { formattedMoney } from '~utils/formatted/number';
import { isEqual } from '~utils/lodash';

interface IContext extends Context {
  dataset: CustomChartDataset;
}

type CustomChartDataset = ChartDataset & {
  customData: number[];
};

type ShowData = {
  avg: number;
  min: number;
  max: number;
  count: number;
  isDataEmpty: boolean;
};
ChartJS.register(ChartDataLabels, CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface TooltipProps {
  opacity: number;
  top: number;
  left: number;
  dataIndex?: number;
  value: {
    avg: number;
    min: number;
    max: number;
  };
}
interface StakeBarGraphProps {
  graphData: IWikiChart;
  onClickChart?: (event: ChartEvent | null, chartIndex: number) => void;
}

interface UseGraphDataProps extends StakeBarGraphProps {}

function StakeBarGraph({ graphData, onClickChart }: StakeBarGraphProps) {
  const chartRef = useRef(null);
  const { data, options, tooltip } = useGraphData({ graphData, onClickChart });

  const handleClickTooltip = useCallback(() => {
    if (onClickChart && tooltip.dataIndex !== undefined) {
      onClickChart(null, tooltip.dataIndex);
    }
  }, [onClickChart, tooltip]);

  return (
    <StakeBarGraphWrapper>
      <GraphWapper>
        {/* @ts-ignore */}
        <Bar data={data} options={options} ref={chartRef} />
        <GraphTooltip
          left={tooltip.left}
          top={tooltip.top}
          opacity={tooltip.opacity}
          onClick={handleClickTooltip}>
          <div>
            <p>
              {formattedMoney(tooltip.value.min)}만원 ~ {formattedMoney(tooltip.value.max)}만원
            </p>
            <p className='avg'>
              {tooltip.value.avg > 0 ? `평균 ${formattedMoney(tooltip.value.avg)}만원` : '정보없음'}
              {' >'}
            </p>
          </div>
        </GraphTooltip>
      </GraphWapper>
    </StakeBarGraphWrapper>
  );
}

export default memo(StakeBarGraph);

function useGraphData({ graphData, onClickChart }: UseGraphDataProps) {
  const { isMobile } = useMedia();
  const [tooltip, setTooltip] = useState<TooltipProps>({
    opacity: 0,
    top: 0,
    left: 0,
    dataIndex: undefined,
    value: { avg: 0, min: 0, max: 0 },
  });
  const theme = useTheme();

  const externalTooltipHandler = (context: {
    chart: Context;
    tooltip: TooltipModel<ChartType>;
  }) => {
    const tooltipModel = context.tooltip;

    if (tooltipModel.opacity === 0) {
      setTooltip((prev) => ({ ...prev, opacity: 0 }));

      return;
    }

    const dataIndex: number = tooltipModel.dataPoints[0].dataIndex;
    // @ts-ignore
    const data = tooltipModel.dataPoints[0].dataset.showData[dataIndex] as ShowData[];

    // TODO label Data를 array to object로 변경해서 직관성있게 수정
    // @ts-ignore
    const value = { avg: data.avg, min: data.min, max: data.max };

    /** 해당 그래프의 WIKI 값의 데이터가 없을때 count = 1, avg = 0 */
    // @ts-ignore
    if (data.count < 2) {
      return;
    }

    const newTooltipData: TooltipProps = {
      opacity: 1,
      left: tooltipModel.caretX - 65,
      top: tooltipModel.caretY - 65,
      dataIndex,
      value,
    };

    if (!isEqual(tooltip, newTooltipData)) setTooltip(newTooltipData);
  };

  const options = {
    maintainAspectRatio: false,
    devicePixelRatio: 2,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    onClick: (event: ChartEvent, chartElements: ActiveElement[]) => {
      // @ts-ignore
      const datasets = event?.chart.tooltip.dataPoints;
      if (datasets) {
        if (onClickChart && !isMobile) {
          onClickChart(event, chartElements[0].index);
        }
      }
    },
    onHover: (event: ChartEvent, chartElement: ActiveElement[]) => {
      // @ts-ignore
      const datasets = event?.chart.tooltip.dataPoints;
      if (datasets) {
        const target = event.native?.target as HTMLStyleElement;
        target.style.cursor = chartElement[0] ? 'pointer' : 'default';
      }
    },
    animation: {
      duration: 0,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: (context: { chart: Context; tooltip: TooltipModel<ChartType> }) =>
          externalTooltipHandler(context),
      },
      datalabels: {
        labels: {
          value: {
            color: (ctx: IContext) => {
              const datasets = ctx.dataset;
              const index = ctx.dataIndex;
              // @ts-ignore
              const showData: ShowData = datasets.showData[index];

              if (showData.count >= 2) {
                return theme.colors.magenta700;
              }

              return theme.colors.gray800;
            },
            font: {
              weight: '500',
              lineHeight: '12px',
              size: '12px',
              family: 'Pretendard',
            },
            align: 'end',
            textAlign: 'center',
            anchor: 'end',
            formatter: (value: number | number[], ctx: IContext) => {
              const datasets = ctx.dataset;
              const index = ctx.dataIndex;
              // @ts-ignore
              const showData: ShowData = datasets.showData[index];

              if (datasets.order === 2) {
                if (showData.count >= 2) {
                  if (showData.avg === 0 && (showData.min > 0 || showData.max > 0)) {
                    return '';
                  } else {
                    return `± ${formattedMoney(showData.avg)}`;
                  }
                }
              }
              if (datasets.order === 1) {
                if (showData.count >= 2) {
                  if (showData.avg === 0 && (showData.min > 0 || showData.max > 0)) {
                    return '정보없음';
                  } else {
                    return ' ';
                  }
                }

                if (showData.avg <= 0 && showData.count < 2) {
                  return '정보없음';
                }

                if (showData.avg > 0) {
                  return formattedMoney(showData.avg);
                }
              }

              return ' ';
            },
          },
        },
      },
    },
    layout: {
      padding: {
        top: 20,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          font: {
            weight: '500',
            lineHeight: '12px',
            size: '10px',
            family: 'Pretendard',
          },
          color: theme.colors.gray700,
        },
      },
      y: {
        grid: {
          drawBorder: false,
        },
        ticks: {
          crossAlign: 'center',
          autoSkip: false,
          display: true,
          tickLength: 10,
          padding: 12,
          font: {
            weight: '500',
            lineHeight: '12px',
            size: '10px',
            family: 'Pretendard',
          },
          color: theme.colors.gray700,
          callback: (value: number) => {
            const fommatedAxisLabel = [2000, 4000, 6000, 8000, 10000];
            const formattedYScale = () => {
              if (value >= 10000) {
                return '1억';
              } else {
                return value / 1000 + '천';
              }
            };

            return fommatedAxisLabel.includes(value) ? formattedYScale() : '';
          },
          stepSize: 1000,
        },
        min: 0,
        max: 10000,
      },
    },
  };

  const data = {
    labels: graphData.map((wikiSalary) => wikiSalary.title),
    datasets: [
      {
        backgroundColor: theme.colors.magenta500,
        borderColor: theme.colors.magenta500,
        borderWidth: 1,
        barThickness: 30,
        data: graphData.map((wikiSalary) => [
          wikiSalary.showData.avg,
          wikiSalary.showData.avg > 0 ? wikiSalary.showData.avg - 250 : 250,
        ]),
        stakeData: graphData.map((wikiSalary) => [
          wikiSalary.showData.max,
          wikiSalary.showData.min,
        ]),
        showData: graphData.map((wikiSalary) => ({
          isDataEmpty: wikiSalary.isDataEmpty,
          max: wikiSalary.stakeData.max,
          min: wikiSalary.stakeData.min,
          avg: wikiSalary.data,
          count: wikiSalary.showData.count,
        })),
        pointHitRadius: 20,
        order: 1,
      },
      {
        backgroundColor: theme.colors.yellow300,
        borderColor: theme.colors.yellow300,
        borderWidth: 1,
        barThickness: 30,
        data: graphData.map((wikiSalary) => [
          wikiSalary.showData.max,
          wikiSalary.showData.min,
          wikiSalary.showData.avg,
        ]),
        showData: graphData.map((wikiSalary) => ({
          isDataEmpty: wikiSalary.isDataEmpty,
          max: wikiSalary.stakeData.max,
          min: wikiSalary.stakeData.min,
          avg: wikiSalary.data,
          count: wikiSalary.showData.count,
        })),
        order: 2,
      },
    ],
  };

  return { options, data, tooltip };
}

const GraphWapper = styled.div`
  position: relative;
  margin: 0 auto;
  width: 100%;

  canvas {
    width: 100%;
    height: 160px;
  }

  ${({ theme }) => theme.media.with('mobile', 'tablet')} {
    width: 100%;

    canvas {
      width: 100% !important;
      height: 160px !important;
    }
  }
`;

const StakeBarGraphWrapper = styled.div`
  display: flex;
  justify-content: center;
  ul {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    list-style: none;
    margin-top: 17px;
    margin-right: 12px;
    margin-bottom: 40px;
  }

  li {
    font-size: 10px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.gray800};
    width: 50px;
    line-height: 12px;
    margin-bottom: 15px;
  }
  position: relative;
`;

const GraphTooltip = styled.div<{ top: number; left: number; opacity: number }>`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 130px;
  padding: 6px 0;
  background-color: ${({ theme }) => theme.colors.magenta700};
  border-radius: 4px;
  color: white;
  transition: all 0.1s ease;
  pointer-events: none;

  top: ${(props) => props.top}px;
  left: ${(props) => props.left}px;
  opacity: ${(props) => props.opacity};

  font-weight: 700;
  font-size: 10px;
  line-height: 12px;

  ${({ theme }) => theme.media.mobile} {
    pointer-events: inherit;
  }

  div {
    position: relative;

    &:after {
      content: '';
      position: absolute;
      right: calc(50% - 7px);

      left: 0;
      right: 0;
      margin: 0 auto;
      width: 0;
      height: 0;

      border-top: 10px solid;
      border-left: 15px solid transparent;
      border-right: 15px solid transparent;
      border-top-color: ${(props) => props.theme.colors.magenta700};
    }
  }

  .avg {
    font-size: 12px;
    line-height: 15px;
  }
`;
